import React from 'react';
import styled, { css } from 'styled-components/macro';

import { findString } from 'helpers/findString';

import { ReactComponent as RegionInfoIcon } from 'icons/icon-info.svg';

import RegionSearchableMenu from 'components/base/Menu/RegionMenu/RegionMenu';
import Span from 'components/elements/span';
import Box from 'components/elements/Box';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import Checkbox from 'components/base/Checkbox/Checkbox';

import theme from 'styles/themes/variables';
import colors from 'styles/themes/colors';
import variables from 'styles/themes/variables';
import { ThemeProps } from 'types/styled';
import getLabel, { getDefinition } from './helpers/getLabel';
import DropdownButton from '../DropdownButton';
import DropdownWrapper from '../DropdownWrapper';
import { ListLabelSpan } from '../shared/styles';
import { IRegionWithLabel } from '../shared/shared';
import { IRegionProps } from 'types/shared';
import { scrollBar } from 'styles/base-classes';

const { spacing } = theme;

export const FlagImage = styled.img<ThemeProps<'img'>>`
  display: inline-flex;
  margin-right: ${spacing.xxsmall};

  @media screen and (max-width: 47em) {
    margin: -0.2em 0.43em 0 -0.2em;
  }
`;

const hoveredStyles = (): string => `
  background: ${colors.Sand};
`;

const ListItemBox = styled(Box)<ThemeProps<'div'>>`
  ${({ isActive, isHovered }): any => css`
    cursor: pointer;
    background: ${isActive || isHovered ? colors.Sand : 'white'};

    span {
      display: inline-block;
      transition: ${`150ms ${variables.timingFunction} transform`};
    }

    &:hover,
    &[aria-selected='true'] {
      ${hoveredStyles}
    }

    ${isHovered && hoveredStyles}
  `}
`;

export const TooltipTitleBox = styled(Box)`
  min-width: 120px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 0.5em 0 0.25em;
`;

export const TerritoryTitle = styled('h3')`
  font-size: 14px;
  line-height: 19px;
  font-weight: 700;
  padding: 0 ${({ theme }): string => theme.spacing.small};
`;

export const EditBtn = styled.div<{ fromTooltip?: boolean }>`
  font-size: ${({ fromTooltip }): string => (fromTooltip ? '14px' : '13px')};
  line-height: 19px;
  font-weight: ${({ fromTooltip }): number => (fromTooltip ? 700 : 600)};
  color: ${colors.AccentGreen};
  cursor: pointer;
`;

export const ListOfTooltipEntities = styled(Box)`
  max-height: 280px;
  overflow-y: auto;
  padding: 0 ${({ theme }): string => theme.spacing.small};
  font-weight: 400;

  ${scrollBar};
`;

export const SavedItem = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 3px 0px;
`;

const TooltipContent = ({
  regionCountries,
  customRegionId,
  switchToEditView,
  regionName,
}: {
  regionCountries: string;
  customRegionId?: string;
  switchToEditView: (id: string) => void;
  regionName: string;
}): JSX.Element => {
  const onClickFunction = (): void => {
    if (customRegionId) switchToEditView(customRegionId);
  };

  return (
    <Box>
      <TooltipTitleBox>
        <TerritoryTitle>{regionName}</TerritoryTitle>
        {customRegionId && (
          <EditBtn fromTooltip onClick={onClickFunction}>
            Edit
          </EditBtn>
        )}
      </TooltipTitleBox>
      <ListOfTooltipEntities>{regionCountries}</ListOfTooltipEntities>
    </Box>
  );
};

const getListItem = (switchToEditView: (id: string) => void): React.FC<any> => {
  const ListItem: React.FC<IListItemProps> = (props) => {
    const { item, isActive, isHovered, isSmallScreen, customRegionId } = props;

    const regionCountries = item?.countries
      .reduce(
        (countries: string[], country: any) => [
          ...countries,
          country.displayName,
        ],
        []
      )
      .sort()
      .join('\n');

    return (
      <ListItemBox
        isActive={false}
        isHovered={isHovered}
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        alignContent="stretch"
        paddingRight={3}
        paddingLeft={3}
        py={2}
      >
        <Box
          alignSelf="center"
          display="inline-block"
          marginLeft="2px"
          marginRight="-2px"
        >
          <Checkbox
            enabledText="Select"
            isChecked={!!isActive}
            insideDropdown
          />
        </Box>
        <Span marginLeft={1} alignSelf="auto">
          {getLabel(item.label, item.description)}
        </Span>

        {!isSmallScreen && regionCountries.length > 0 && (
          <Span marginLeft="auto" onClick={(e): any => e.stopPropagation()}>
            <TippyToolTip
              tooltipContent={
                <TooltipContent
                  regionName={getDefinition(item.label, item.description)}
                  regionCountries={regionCountries}
                  customRegionId={customRegionId}
                  switchToEditView={switchToEditView}
                />
              }
              delay={[250, 250]}
              placement="right"
              maxWidth="max-content"
              enabled
            >
              <Box alignSelf="auto" display="flex" ml={3}>
                <RegionInfoIcon display="inline-flex" width={16} height={16} />
              </Box>
            </TippyToolTip>
          </Span>
        )}

        {isSmallScreen && customRegionId && regionCountries.length > 0 && (
          <Span marginLeft="auto">
            <Box
              alignSelf="auto"
              display="flex"
              ml={3}
              mr={1}
              onClick={(): void => switchToEditView(customRegionId)}
            >
              <EditBtn>Edit</EditBtn>
            </Box>
          </Span>
        )}
      </ListItemBox>
    );
  };

  return ListItem;
};

const getButtonWithDefaultLabel = (
  clearFilter: () => void,
  defaultLabel?: string
): React.FC<any> => {
  const LabeledButton: React.FC<any> = (props: any) => {
    return (
      <DropdownButton
        {...props}
        defaultLabel={defaultLabel}
        dropdownType="Region"
        clearFilter={clearFilter}
      />
    );
  };
  return LabeledButton;
};

const RegionDropdown: React.FC<IRegionDropdownProps> = (props) => {
  const {
    items,
    selectedItem,
    selectedItems,
    onChange,
    disabled,
    itemToString,
    isSmallScreen,
    defaultLabel,
    children,
    isDisplayedWithFlag = false,
    disabledMessage,
    isStretched,
    maxWidth,
    zIndex,
    placement,
    clearFilter,
    onClose,
    isOpened,
    customRegions,
    switchToEditView,
  } = props;
  const matchByRegionId = (
    item: IRegionWithLabel,
    searchValue: string
  ): boolean => {
    return findString(searchValue)(item.displayName);
  };

  const selectedItemsIds = selectedItems.map(
    (item: IRegionWithLabel): string => item.id
  );

  const itemsWithoutSelected = items.filter(
    (item: IRegionWithLabel) => !selectedItemsIds.includes(item.id)
  );

  const customItemsWithoutSelected = customRegions.filter(
    (item: IRegionWithLabel) => !selectedItemsIds.includes(item.id)
  );

  // selectedItem can be undefined which will trigger React into thinking this is a controlled component,
  // when it then switches to an actual value runtime errors will be thrown since React will figure
  // we're trying to switch a conrtolled component into an uncontrolled component. Null is a valid value,
  // therefore we add this seemingly weird check.
  return (
    <DropdownWrapper
      hasWideSearchableMenu
      isEnabled={!disabled}
      label="REGION VIEW"
    >
      <RegionSearchableMenu
        items={itemsWithoutSelected}
        myItems={customItemsWithoutSelected}
        selectedItem={!disabled ? selectedItem || null : null}
        selectedItems={!disabled ? selectedItemsIds : []}
        onChange={onChange}
        disabled={disabled}
        itemToString={itemToString}
        Button={getButtonWithDefaultLabel(clearFilter, defaultLabel)}
        Item={getListItem(switchToEditView)}
        additionalFindMethod={matchByRegionId}
        isDisplayedWithFlag={isDisplayedWithFlag}
        isOpenDropdownDef={isOpened}
        ListLabel={ListLabelSpan}
        disabledMessage={disabledMessage}
        isSmallScreen={isSmallScreen}
        maxWidth={maxWidth}
        zIndex={zIndex}
        placement={placement}
        isMenuButtonStretched={isStretched}
        multipleSelections={selectedItems}
        textToSelect="Region"
        isMultipleSelection
        hideRecentBlock
        onClose={onClose}
      >
        {children}
      </RegionSearchableMenu>
    </DropdownWrapper>
  );
};

interface IRegionDropdownProps {
  items: IRegionWithLabel[];
  selectedItem: IRegionWithLabel;
  onChange: (item: IRegionProps) => void;
  disabled?: boolean;
  itemToString?: (item: any) => string | null;
  isSmallScreen: boolean;
  defaultLabel?: string;
  children?: any;
  isDisplayedWithFlag?: boolean;
  disabledMessage?: string;
  showListLabel?: boolean;
  /** Max width of the tooltip for the dropdown, zIndex of the tooltip block,
   * placement is for place of tooltip relatively the block, isStretched is to stretch the block in tooltip wrapper */
  maxWidth?: string;
  zIndex?: string;
  placement?: string;
  isStretched?: boolean;
  selectedItems: IRegionWithLabel[];
  clearFilter: () => void;
  onClose: () => void;
  isOpened: boolean;
  customRegions: IRegionWithLabel[];
  switchToEditView: (customRegionId: string) => void;
}

interface IListItemProps {
  item: any;
  isActive?: boolean;
  isHovered?: boolean;
  isSmallScreen?: boolean;
  isDisplayedWithFlag?: boolean;
  isCustomRegion?: boolean;
  customRegionId?: string;
}

export default RegionDropdown;
